import React, { useEffect, useRef, useState } from "react";
import { NodeViewProps, NodeViewWrapper } from "@tiptap/react";

import {
  Paper,
  Popper,
  ClickAwayListener,
  Button,
  IconButton,
  Divider,
} from "@material-ui/core";

import {
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  DeleteOutline,
  Close,
} from "@material-ui/icons";

type Props = NodeViewProps;

const ImageNodeView: React.FC<Props> = ({
  node,
  selected,
  updateAttributes,
  deleteNode,
}) => {
  const [toolbarOpen, setToolbarOpen] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const toolbarAnchorRef = useRef<HTMLDivElement>(null);
  const currentRotation = node.attrs.rotation || 0;
  const alignment = node.attrs.alignment || "left";
  const currentWidth = node.attrs.width || "800px";

  const getAlignmentStyle = (align: string): React.CSSProperties => {
    switch (align) {
      case "center":
        return {
          display: "block",
          marginLeft: "auto",
          marginRight: "auto",
          textAlign: "center",
        };
      case "right":
        return {
          display: "block",
          marginLeft: "auto",
          marginRight: "0",
          textAlign: "right",
        };
      case "left":
      default:
        return {
          display: "block",
          marginLeft: "0",
          marginRight: "auto",
          textAlign: "left",
        };
    }
  };

  const getResizeCursor = (direction: string, rotation: number) => {
    // Only keeping base angles for corners
    const baseAngles: Record<string, number> = {
      "bottom-right": 45,
      "bottom-left": 135,
      "top-left": 225,
      "top-right": 315,
    };

    let angle = ((baseAngles[direction] || 0) + rotation) % 360;
    if (angle < 0) angle += 360;

    const step = Math.round(angle / 45) % 8;

    const cursors = [
      "ew-resize",
      "nwse-resize",
      "ns-resize",
      "nesw-resize",
      "ew-resize",
      "nwse-resize",
      "ns-resize",
      "nesw-resize",
    ];

    return cursors[step];
  };

  // Image-Click Logic
  useEffect(() => {
    if (!selected || !containerRef.current) {
      setToolbarOpen(false);
      return;
    }
    const imageElement = containerRef.current;
    /* * This is the actual scrolling viewport of your editor. */ const editorViewport =
      document.querySelector(".docgen-editor-canvas") as HTMLElement | null;
    if (!editorViewport) {
      return;
    }
    const updateToolbarVisibility = () => {
      const imageRect = imageElement.getBoundingClientRect();
      const editorRect = editorViewport.getBoundingClientRect();
      /* * Image is completely above the visible editor area. */ const passedTop =
        imageRect.bottom <= editorRect.top;
      /* * Image is completely below the visible editor area. */ const passedBottom =
        imageRect.top >= editorRect.bottom;
      /* * Toolbar is visible only while the image is * inside the visible editor viewport. */ const imageVisible =
        !passedTop && !passedBottom;
      setToolbarOpen(selected && imageVisible);
    };
    /* * Check immediately. */ updateToolbarVisibility();
    /* * Listen for scrolling anywhere inside the editor. * * Capture = true ensures this also catches the * scroll event from nested scrolling elements. */ document.addEventListener(
      "scroll",
      updateToolbarVisibility,
      true,
    );
    window.addEventListener("resize", updateToolbarVisibility);
    return () => {
      document.removeEventListener("scroll", updateToolbarVisibility, true);
      window.removeEventListener("resize", updateToolbarVisibility);
    };
  }, [selected]);
  const handleImageClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    setToolbarOpen(true);
  };

  const handleClickAway = () => {
    setToolbarOpen(false);
  };

  const handleAlignmentChange = (newAlignment: "left" | "center" | "right") => {
    updateAttributes({
      alignment: newAlignment,
    });

    setToolbarOpen(true);
  };

  const handleSizeChange = (size: string) => {
    const image = imageRef.current;

    if (!image) {
      return;
    }

    /*
     * Find the actual available width of the editor/page.
     *
     * We intentionally don't use image.offsetWidth here because
     * the image may already have been resized.
     */
    const pageContent = image.closest(
      ".docgen-page-content",
    ) as HTMLElement | null;

    const proseMirror = image.closest(".ProseMirror") as HTMLElement | null;

    const editorContainer = pageContent || proseMirror;

    if (!editorContainer) {
      return;
    }

    const availableWidth = editorContainer.clientWidth;

    if (!availableWidth) {
      return;
    }

    const percentage = parseFloat(size);

    if (isNaN(percentage)) {
      return;
    }

    /*
     * Calculate the target width from the page/editor width.
     *
     * 25%  -> page width * 0.25
     * 50%  -> page width * 0.50
     * 75%  -> page width * 0.75
     * 100% -> page width
     */
    const targetWidth = (availableWidth * percentage) / 100;

    /*
     * Preserve the current image aspect ratio.
     */
    const naturalWidth = image.naturalWidth || image.offsetWidth;

    const naturalHeight = image.naturalHeight || image.offsetHeight;

    if (!naturalWidth || !naturalHeight) {
      return;
    }

    const aspectRatio = naturalWidth / naturalHeight;

    const targetHeight = targetWidth / aspectRatio;

    updateAttributes({
      width: `${Math.round(targetWidth)}px`,
      height: `${Math.round(targetHeight)}px`,
    });
  };

  const handleDelete = () => {
    setToolbarOpen(false);
    deleteNode();
  };

  // --- Resize Logic (Preserved As-Is) ---
  const startResize = (event: React.MouseEvent, direction: string) => {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startY = event.clientY;

    const aspectRatio =
      (imageRef.current?.naturalWidth || 1) /
      (imageRef.current?.naturalHeight || 1);

    const parseDimension = (val: any) => {
      if (!val) return NaN;
      return parseFloat(String(val).replace("px", ""));
    };

    let startWidth = parseDimension(node.attrs.width);
    if (isNaN(startWidth)) {
      startWidth = imageRef.current?.offsetWidth || 300;
    }

    let startHeight = parseDimension(node.attrs.height);
    if (isNaN(startHeight)) {
      startHeight = startWidth / aspectRatio;
    }

    const isLeft = direction.includes("left");
    const isTop = direction.includes("top");

    const angle = currentRotation * (Math.PI / 180);

    const onMouseMove = (e: MouseEvent) => {
      const screenDx = e.clientX - startX;
      const screenDy = e.clientY - startY;

      const localDx = screenDx * Math.cos(angle) + screenDy * Math.sin(angle);
      const localDy = -screenDx * Math.sin(angle) + screenDy * Math.cos(angle);

      const deltaX = isLeft ? -localDx : localDx;
      const deltaY = isTop ? -localDy : localDy;

      let newWidth = startWidth;
      let newHeight = startHeight;

      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        newWidth = Math.max(50, startWidth + deltaX);
        newHeight = newWidth / aspectRatio;
      } else {
        newHeight = Math.max(50, startHeight + deltaY);
        newWidth = newHeight * aspectRatio;
      }

      updateAttributes({
        width: `${newWidth}px`,
        height: `${newHeight}px`,
      });
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  // Shared styles for the circular handles
  const handleStyle: React.CSSProperties = {
    position: "absolute",
    width: 12,
    height: 12,
    borderRadius: "50%",
    border: "1px solid #777",
    background: "#fff",
    zIndex: 10,
  };

  const safeWidth = node.attrs.width?.toString().includes("%")
    ? "250px"
    : node.attrs.width || "auto";

  return (
    <NodeViewWrapper
      as="div"
      data-alignment={alignment}
      data-drag-handle
      style={{
        position: "relative",
        ...getAlignmentStyle(alignment),
        lineHeight: 0,
        fontSize: 0,
        margin:
          alignment === "center"
            ? "8px auto"
            : alignment === "right"
              ? "8px 0 8px auto"
              : "8px auto 8px 0",
      }}
    >
      <ClickAwayListener onClickAway={handleClickAway}>
        <div
          ref={toolbarAnchorRef}
          style={{
            position: "relative",
            display: "inline-block",
          }}
        >
          {/* =====================================================
              FLOATING IMAGE TOOLBAR
          ====================================================== */}
          <Popper
            open={toolbarOpen && selected}
            anchorEl={toolbarAnchorRef.current}
            placement="top"
            disablePortal={false}
            modifiers={{
              preventOverflow: {
                enabled: true,
                boundariesElement: "viewport",
                padding: 12,
              },
              flip: {
                enabled: true,
                behavior: ["top", "bottom"],
                padding: 12,
              },
              offset: {
                enabled: true,
                offset: "0, 8",
              },
            }}
            style={{
              zIndex: 1300,
            }}
          >
            <Paper
              elevation={0}
              style={{
                position: "relative",
                background: "#141414",
                color: "#fff",
                borderRadius: 8,
                padding: "4px 8px",
                paddingRight: 12,
                display: "flex",
                alignItems: "center",
                gap: 4,
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.35)",
                marginBottom: 8,
              }}
              onMouseDown={(e) => {
                /*
                 * Prevent the toolbar click from removing
                 * the TipTap image selection.
                 */
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <IconButton
                size="small"
                onClick={() => setToolbarOpen(false)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                style={{
                  position: "absolute",
                  top: -10,
                  right: -10,
                  width: 22,
                  height: 22,
                  padding: 2,
                  color: "#fff",
                  backgroundColor: "#444",
                  border: "1px solid rgba(255,255,255,0.35)",
                  borderRadius: "50%",
                  zIndex: 20,
                }}
              >
                <Close style={{ fontSize: 10 }} />
              </IconButton>

              {/* =================================================
                  SIZE BUTTONS
              ================================================== */}
              {["25%", "50%", "75%", "100%"].map((size) => {
                const pageContent = imageRef.current?.closest(
                  ".docgen-page-content",
                ) as HTMLElement | null;

                const proseMirror = imageRef.current?.closest(
                  ".ProseMirror",
                ) as HTMLElement | null;

                const editorContainer = pageContent || proseMirror;

                const availableWidth = editorContainer?.clientWidth || 0;

                const currentWidthPx = parseFloat(
                  String(currentWidth).replace("px", ""),
                );

                const selectedPercentage =
                  availableWidth > 0 && !isNaN(currentWidthPx)
                    ? (currentWidthPx / availableWidth) * 100
                    : 0;

                const presetPercentage = parseFloat(size);

                /*
                 * Allow a small tolerance because the actual width
                 * is rounded when handleSizeChange() calculates it.
                 */
                const active =
                  Math.abs(selectedPercentage - presetPercentage) < 2;

                return (
                  <Button
                    key={size}
                    size="small"
                    onClick={() => handleSizeChange(size)}
                    style={{
                      color: active ? "#fff" : "#aaa",
                      backgroundColor: active ? "#3774ee" : "transparent",
                      fontSize: 12,
                      fontWeight: 600,
                      minWidth: "auto",
                      padding: "4px 10px",
                      borderRadius: 5,
                      textTransform: "none",
                    }}
                  >
                    {size}
                  </Button>
                );
              })}

              <Divider
                orientation="vertical"
                style={{
                  height: 18,
                  backgroundColor: "rgba(255,255,255,.2)",
                  margin: "0 4px",
                }}
              />

              {/* =================================================
                  ALIGN LEFT
              ================================================== */}
              <IconButton
                size="small"
                onClick={() => handleAlignmentChange("left")}
                style={{
                  color: alignment === "left" ? "#fff" : "#aaa",
                  backgroundColor:
                    alignment === "left" ? "#3774ee" : "transparent",
                  padding: 6,
                  borderRadius: 5,
                }}
              >
                <FormatAlignLeft fontSize="small" />
              </IconButton>

              {/* =================================================
                  ALIGN CENTER
              ================================================== */}
              <IconButton
                size="small"
                onClick={() => handleAlignmentChange("center")}
                style={{
                  color: alignment === "center" ? "#fff" : "#aaa",
                  backgroundColor:
                    alignment === "center" ? "#3774ee" : "transparent",
                  padding: 6,
                  borderRadius: 5,
                }}
              >
                <FormatAlignCenter fontSize="small" />
              </IconButton>

              {/* =================================================
                  ALIGN RIGHT
              ================================================== */}
              <IconButton
                size="small"
                onClick={() => handleAlignmentChange("right")}
                style={{
                  color: alignment === "right" ? "#fff" : "#aaa",
                  backgroundColor:
                    alignment === "right" ? "#3774ee" : "transparent",
                  padding: 6,
                  borderRadius: 5,
                }}
              >
                <FormatAlignRight fontSize="small" />
              </IconButton>

              <Divider
                orientation="vertical"
                style={{
                  height: 18,
                  backgroundColor: "rgba(255,255,255,.2)",
                  margin: "0 4px",
                }}
              />

              {/* =================================================
                  DELETE IMAGE
              ================================================== */}
              <IconButton
                size="small"
                onClick={handleDelete}
                style={{
                  color: "#aaa",
                  padding: 6,
                }}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Paper>
          </Popper>

          {/* =====================================================
              EXISTING IMAGE CONTAINER
          ====================================================== */}
          <div
            ref={containerRef}
            onClick={handleImageClick}
            style={{
              position: "relative",
              transformOrigin: "center center",
              transform: `rotate(${currentRotation}deg)`,
              outline: selected ? "1px solid #777" : "1px solid transparent",
              display: "inline-block",
              lineHeight: 0,
            }}
          >
            {/* =================================================
                IMAGE
            ================================================== */}
            <img
              ref={imageRef}
              src={node.attrs.src}
              alt={node.attrs.alt || ""}
              style={{
                display: "block",
                width: safeWidth,
                height: node.attrs.height || "auto",
                userSelect: "none",
                maxWidth: "100%",
              }}
            />

            {/* =================================================
                CORNER RESIZE HANDLES
            ================================================== */}
            {selected && (
              <>
                {/* TOP LEFT */}
                <div
                  onMouseDown={(e) => startResize(e, "top-left")}
                  style={{
                    ...handleStyle,
                    top: -6,
                    left: -6,
                    cursor: getResizeCursor("top-left", currentRotation),
                  }}
                />

                {/* TOP RIGHT */}
                <div
                  onMouseDown={(e) => startResize(e, "top-right")}
                  style={{
                    ...handleStyle,
                    top: -6,
                    right: -6,
                    cursor: getResizeCursor("top-right", currentRotation),
                  }}
                />

                {/* BOTTOM LEFT */}
                <div
                  onMouseDown={(e) => startResize(e, "bottom-left")}
                  style={{
                    ...handleStyle,
                    bottom: -6,
                    left: -6,
                    cursor: getResizeCursor("bottom-left", currentRotation),
                  }}
                />

                {/* BOTTOM RIGHT */}
                <div
                  onMouseDown={(e) => startResize(e, "bottom-right")}
                  style={{
                    ...handleStyle,
                    bottom: -6,
                    right: -6,
                    cursor: getResizeCursor("bottom-right", currentRotation),
                  }}
                />
              </>
            )}
          </div>
        </div>
      </ClickAwayListener>
    </NodeViewWrapper>
  );
};

export default ImageNodeView;
